// HeroSlider.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Typography, alpha, Skeleton } from "@mui/material";
import { keyframes } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { sliderBadges } from "../HeroData";

const AUTOPLAY_MS = 6000;
const FILE_BASE_URL = import.meta.env.VITE_FILE_API;

const kenBurns = keyframes`
  from { transform: scale(1); }
  to   { transform: scale(1.09); }
`;

const floatY = keyframes`
  0%   { transform: translateY(0); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0); }
`;

function buildImageUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl; // اگه بک‌اند بعداً url کامل بده
  return `${FILE_BASE_URL}${imageUrl}`;
}

export default function HeroSlider({
  sliderData,
  isLoading,
  isFetching,
  isError,
}) {
  

  // دیتای خام رو به شکلی که اسلایدر نیاز داره تبدیل می‌کنیم:
  // فقط اسلایدهای فعال position=homeTop رو نگه می‌داریم و بر اساس order مرتب می‌کنیم
  const slides = useMemo(() => {
    const raw = sliderData?.data ?? [];
    return raw
      .filter(
        (item) =>
          item.type === "slider" &&
          item.position === "homeTop" &&
          item.isActive,
      )
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        id: item._id,
        title: item.title,
        // فقط "link" واقعیه — pathForPage تو ادمین ممکنه یه مقدار پیش‌فرض داشته باشه
        // حتی وقتی کاربر لینکی انتخاب نکرده، پس نباید بهش تکیه کنیم
        link: item.link || "",
        // بک‌اند تصویر رو به شکل آرایه‌ی "images" برمی‌گردونه، نه یه رشته‌ی "imageUrl"
        image: buildImageUrl(item.images?.[0]),
      }));
  }, [sliderData]);

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const goToSlide = useCallback(
    (index) => {
      if (!slides.length) return;
      setCurrent((index + slides.length) % slides.length);
    },
    [slides.length],
  );

  const changeSlide = useCallback(
    (dir) => goToSlide(current + dir),
    [current, goToSlide],
  );

  useEffect(() => {
    if (!slides.length || paused) return undefined;
    let timer;
    // Delay initial autoplay start to avoid competing with main-thread bootstrap
    const initialDelay = setTimeout(() => {
      timer = setInterval(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
      }, AUTOPLAY_MS);
    }, 6000);

    return () => {
      clearTimeout(initialDelay);
      if (timer) clearInterval(timer);
    };
  }, [slides.length, paused]);

  if (isLoading || slides.length === 0) {
    if (isError) return null;
    return (
      <Box sx={{ flex: 1, width: "100%" }}>
        <Skeleton
          variant="rounded"
          sx={{
            width: "100%",
            height: "auto",
            aspectRatio: "1 / 0.82",
            borderRadius: "28px",
          }}
        />
        <Box sx={{ height: 7, mt: "18px" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, width: "100%" }}>
      {/* این باکس عمداً overflow ندارد — تا بج‌های شناور کامل دیده بشن.
          فقط باکس داخلی (فریم عکس) overflow:hidden داره. */}
      <Box
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        sx={{ position: "relative" }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "1 / 0.82",
            borderRadius: "28px",
            overflow: "hidden",
            boxShadow: (theme) =>
              `0 24px 60px ${alpha(theme.palette.primary.main, 0.22)}`,
          }}
        >
          {slides.map((slide, index) => {
            const isActive = index === current;
            const hasLink = Boolean(slide.link);
            return (
              <Box
                key={slide.id}
                {...(hasLink
                  ? { component: RouterLink, to: slide.link }
                  : { component: "div" })}
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  padding: "28px",
                  opacity: isActive ? 1 : 0,
                  transition: "opacity .6s ease",
                  textDecoration: "none",
                  overflow: "hidden",
                  cursor: hasLink ? "pointer" : "default",
                  // فقط اسلاید فعال باید کلیک‌پذیر باشه — بقیه با opacity:0 هنوز
                  // روی هم چیده شدن و بدون این خط، اسلایدِ آخرِ آرایه (که بالاترین
                  // z-order رو داره) کلیک‌ها رو قاپ می‌زد، حتی وقتی دیده نمی‌شد
                  pointerEvents: isActive ? "auto" : "none",
                }}
              >
                {/* لایه تصویر جدا — تا زوم Ken Burns روی محتوا اثر نذاره */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                    animation: isActive
                      ? `${kenBurns} ${AUTOPLAY_MS + 900}ms ease-out forwards`
                      : "none",
                  }}
                >
                  <Box
                    component="img"
                    src={slide.image}
                    alt={slide.title || "اسلایدر راما"}
                    width={600}
                    height={492}
                    fetchpriority={isActive ? "high" : "low"}
                    loading={isActive ? "eager" : "lazy"}
                    decoding={isActive ? "sync" : "async"}
                    sx={{
                      width: "100%",
                      height: "100%",
                      aspectRatio: "1 / 0.82",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0) 40%, rgba(15,23,42,0.62) 100%)",
                      pointerEvents: "none",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    position: "relative",
                    bgcolor: "rgba(255,255,255,0.94)",
                    borderRadius: "16px",
                    padding: "14px 20px",
                    transform: isActive ? "translateY(0)" : "translateY(14px)",
                    opacity: isActive ? 1 : 0,
                    transition: isActive
                      ? "transform .5s ease .15s, opacity .5s ease .15s"
                      : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Dana",
                      fontSize: 15,
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {slide.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          {/* پیکان‌ها — RTL: prev سمت راست ولی به‌سمت راست اشاره می‌کنه،
              next سمت چپ ولی به‌سمت چپ اشاره می‌کنه (جهتش درست شد) */}
          <Box
            onClick={() => changeSlide(-1)}
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              right: "16px",
              width: 42,
              height: 42,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.92)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              zIndex: 5,
              transition: "transform .2s ease",
              "&:hover": { transform: "translateY(-50%) scale(1.08)" },
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1E3A8A"
              strokeWidth="2.3"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </Box>
          <Box
            onClick={() => changeSlide(1)}
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              left: "16px",
              width: 42,
              height: 42,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.92)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              zIndex: 5,
              transition: "transform .2s ease",
              "&:hover": { transform: "translateY(-50%) scale(1.08)" },
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1E3A8A"
              strokeWidth="2.3"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Box>
        </Box>

        {/* بج‌های شیشه‌ای شناور — حالا بیرون از باکس overflow:hidden هستن
            پس دیگه نصفشون بریده نمیشه */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            position: "absolute",
            top: -18,
            insetInlineStart: 24,
            bgcolor: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(10px)",
            borderRadius: "14px",
            px: 2,
            py: 1.25,
            boxShadow: "0 12px 28px rgba(15,23,42,0.16)",
            animation: `${floatY} 5s ease-in-out infinite`,
            zIndex: 6,
          }}
        >
          <Typography
            sx={{
              fontFamily: "YekanPlus",
              fontWeight: 800,
              fontSize: 18,
              color: "primary.main",
              lineHeight: 1,
            }}
          >
            {sliderBadges[0].value}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Dana",
              fontSize: 11.5,
              color: "text.secondary",
            }}
          >
            {sliderBadges[0].label}
          </Typography>
        </Box>

        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            position: "absolute",
            bottom: -18,
            insetInlineEnd: 24,
            bgcolor: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(10px)",
            borderRadius: "14px",
            px: 2,
            py: 1.25,
            boxShadow: "0 12px 28px rgba(15,23,42,0.16)",
            animation: `${floatY} 6s ease-in-out infinite`,
            animationDelay: "1.2s",
            zIndex: 6,
          }}
        >
          <Typography
            sx={{
              fontFamily: "YekanPlus",
              fontWeight: 800,
              fontSize: 18,
              color: "secondary.main",
              lineHeight: 1,
            }}
          >
            {sliderBadges[1].value}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Dana",
              fontSize: 11.5,
              color: "text.secondary",
            }}
          >
            {sliderBadges[1].label}
          </Typography>
        </Box>
      </Box>

      {/* دات‌ها — همون استایل قبلی */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          mt: "18px",
        }}
      >
        {slides.map((slide, index) => (
          <Box
            key={slide.id}
            onClick={() => goToSlide(index)}
            sx={{
              width: index === current ? 22 : 7,
              height: 7,
              borderRadius: index === current ? "4px" : "50%",
              bgcolor: index === current ? "secondary.main" : "divider",
              cursor: "pointer",
              transition: "all .25s ease",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
